import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useImperativeForm } from '../../../hooks/use-bind-async-submit';
import { emailSchema } from '../utils';
import { EmailInput } from './UI';

import type { ReceiptInfoBase } from '../../../configs/receipts';
import type { EmailFieldConfig, ReceiptCompProps } from '../utils';

export interface CarrierMemberReceiptInfo extends ReceiptInfoBase {
  type: 'carrier_member';
  email: string;
}

type CarrierMemberReceiptForm = Omit<CarrierMemberReceiptInfo, 'type'>;

type CarrierMemberReceiptProps = ReceiptCompProps<{
  emailFieldConfig: EmailFieldConfig;
}>;

export const CarrierMemberReceipt = ({
  controlRef,
  emailFieldConfig = 'default',
  storage,
  formMode,
}: CarrierMemberReceiptProps) => {
  const schema =
    emailFieldConfig === 'never'
      ? z.object({})
      : z.object({
          email: emailSchema,
        });

  const {
    register,
    formState: { errors },
    handleSubmit,
    subscribe,
  } = useForm<CarrierMemberReceiptForm>({
    defaultValues: { ...(storage?.get?.() ?? {}) },
    mode: formMode ?? 'onSubmit',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const receiptInfo = storage?.get?.();

        storage?.set?.({
          ...receiptInfo,
          ...values,
          type: 'carrier_member',
        });
      },
    });

    return () => cb();
  }, [subscribe]);

  const formProps = useImperativeForm(
    controlRef,
    errors,
    (submit) => {
      return handleSubmit((data) => {
        return submit({ ...data, type: 'carrier_member' });
      });
    },
    'carrier_member',
  );

  return (
    <form {...formProps}>
      <EmailInput
        config={emailFieldConfig}
        errorMessage={errors.email?.message}
        {...register('email')}
      />
    </form>
  );
};
